<template lang="pug">
  v-container.ma-0.pa-0(fluid)
    v-snackbar( v-model="snackbar" :color="snackbarColor" ) {{ snackbarMessage }}
      template( v-slot:action="{attrs}")
        v-btn(text v-bind="attrs" @click="snackbar = false")
          v-icon fal fa-times
    v-row.ma-0
      v-col(cols="12" md="6")
        v-menu(
          v-model="dateRangeMenu"
          :close-on-content-click="false"
          transition="scale-transition"
          offset-y
          min-width="auto"
        )
          template(v-slot:activator="{ on, attrs }")
            v-text-field(
              :value="dateRangeFormatted"
              outlined
              :label="$t('importAttendanceDialog.dateRangeSelectorLabel')"
              prepend-icon="fas fa-calendar-alt"
              v-bind="attrs"
              v-on="on"
              clearable
              clear-icon="fal fa-times"
              @click:clear="dateRange = []"
              readonly
              dense
              :rules="dateRangeRules"
            )
          v-date-picker(
            v-model="dateRange"
            range
            no-title
            first-day-of-week="1"
            show-week
            prev-icon="fas fa-chevron-left"
            next-icon="fas fa-chevron-right"
            @change="onDateRangeChange"
          )
            v-spacer
            v-btn(@click="dateRangeMenu = false" color="primary") {{ $t('buttons.okButtonLabel') }}

      v-col(cols="12" md="6")
        v-select(
          outlined dense
          append-icon="fas fa-chevron-down"
          :label="$t('StatisticsPage.customerSelectorLabel')"
          v-model="selectedCustomer"
          :items="customerList"
          item-value="id"
          item-text="name"
          :loading="fetchInProgress"
          @change="onSelectedCustomerChange(selectedCustomer)"
          return-object)

    v-row(v-if="!fetchInProgress")
      v-col(cols="12" md="3" v-for="(item,idx) in statisticsList" :key="idx" v-if="statisticsList && statisticsList.length > 0")
        v-card.mb-5(:flat="$vuetify.breakpoint.mdAndDown"  )
          v-card-title.group-header(:class="cardGroupHeaderClass" v-if="item.topMenuGroup && item.topMenuGroup.menuGroup") {{item.topMenuGroup.menuGroup.title}}
          v-card-text.text-center
            table.mt-4.subgroup-table(v-for="(i,idx) in item.subMenuGroups", :key="idx")
              caption {{i.menuGroup.title}}
              thead
                tr
                  th
                  th {{ $t('StatisticsPage.orderedLabel') }}
                  th {{ $t('StatisticsPage.takenLabel') }}
              tbody(v-if="i && i.menuGroupSlots")
                tr(v-for="(a,idx) in i.menuGroupSlots")
                  td.slot-column.font-weight-bold {{a.menuGroupSlot.code}}
                  td.value-column {{a.portionsReservedCount}}
                  td.value-column {{a.portionsDeliveredCount}}

                tr.row-color
                  td.slot-column {{ $t('StatisticsPage.totalLabel') }}
                  td.value-column {{getTotalOrderedPortion(i.menuGroupSlots)}}
                  td.value-column {{getTotalDeliveredPortion(i.menuGroupSlots)}}
      v-col(col="12" v-if="statisticsList && statisticsList.length  < 1")
        v-card.elevation-0
          v-card-text.text-center.title {{$t('StatisticsPage.noStatisticsTitle')}}
    v-skeleton-loader(v-else type="list-item@10" )

</template>

<script lang="ts">
import Vue from 'vue'
import {Component} from 'vue-property-decorator'
import moment from 'moment/moment'
import _ from 'lodash'
import Statistics from "@/model/Statistics";
import {STATISTICS_END_DATE, STATISTICS_START_DATE} from "@/constants";
import {Action} from "vuex-class";
import {FETCH_CUSTOMER_DATA, FETCH_STATISTICS} from "@/store/action-types";
import i18n from "@/i18n";

@Component({
  components: {
  }
})
export default class MenuGroupsAndSlotsStatistics extends Vue {

  @Action(FETCH_CUSTOMER_DATA) fetchCustomerData!: { (): Promise<any[]> }
  @Action(FETCH_STATISTICS) fetchStatisticsData!: { (payload: any): Promise<any[]> }

  private dateRangeMenu: boolean = false
  private dateRange: string[] = []
  private selectedCustomer: any | null = null
  private customerList: any[] = []
  private fetchInProgress: boolean = false
  private statisticsList: Statistics[] = []
  private snackbar: boolean = false
  private snackbarMessage: string = ''
  private snackbarColor: string = ''
  private dateRangeRules: Array<(v: string | null) => boolean | string> = [
    (v: string | null) => !!v || i18n.t('validation.valueIsRequired').toString(),
    (v: string | null) => this.validateDateRange() || i18n.t('StatisticsPage.dateRangeValidationText').toString(),
  ]

  validateDateRange (): boolean {
    return this.dateRange.length == 2
  }

  getTotalOrderedPortion (item: any) {
    let summary: number = 0
    item.forEach((i: any) => {
      summary = summary + i.portionsReservedCount
    })
    return summary
  }

  getTotalDeliveredPortion(item: any) {
    let summary: number = 0
    item.forEach((i: any) => {
      summary = summary + i.portionsDeliveredCount
    })
    return summary
  }

  get dateRangeFormatted(): string {
    return _.chain(this.dateRange)
      .map(date => moment(date))
      .sortBy(date => date.unix())
      .map(date => date.format('D.M.YYYY'))
      .join(' - ')
      .value()
  }

  get cardGroupHeaderClass(): string {
    return this.$vuetify.breakpoint.mdAndUp ? '' : 'card-header-small'
  }

  onDateRangeChange (item: string[]) {
    if(item && item.length > 1 && this.selectedCustomer) {
      localStorage.setItem(STATISTICS_START_DATE, item[0])
      localStorage.setItem(STATISTICS_END_DATE, item[1])
      this.fetchStatistics(item)
    }
  }

  onSelectedCustomerChange (item: any) {
    this.selectedCustomer = item
    if(this.dateRange && this.dateRange.length > 1) {
      this.fetchStatistics(this.dateRange)
    }
  }

  async fetchCustomers(): Promise<void> {
    this.fetchInProgress = true
    try {
      this.customerList = await this.fetchCustomerData()
      this.selectedCustomer = this.customerList[0] ? this.customerList[0] : null
    } catch (e) {
      this.snackbar = true
      this.snackbarMessage = this.$t('sendEmailDialog.fetchDataError').toString()
      this.snackbarColor = 'error'
    } finally {
      this.fetchInProgress = false
    }
  }

  async fetchStatistics(item: string[]): Promise<void> {

    this.fetchInProgress = true
    const payload: any = {
      customer:this.selectedCustomer && this.selectedCustomer.id ?  `/api/customer/${this.selectedCustomer.id}` : `/api/customer/${this.customerList![0].id}` ,
      dateFrom: item[0],
      dateTo: item[1],
    }
    try {
      this.statisticsList = await this.fetchStatisticsData(payload)
    } catch (e) {
      this.snackbar = true
      this.snackbarMessage = this.$t('sendEmailDialog.fetchDataError').toString()
      this.snackbarColor = 'error'
    } finally {
      this.fetchInProgress = false
    }
  }

  async created (): Promise<void> {
    const startDate: string = localStorage.getItem(STATISTICS_START_DATE) ? localStorage.getItem(STATISTICS_START_DATE)! : moment().startOf('month').format('YYYY-MM-DD')
    const endDate: string =  localStorage.getItem(STATISTICS_END_DATE) ? localStorage.getItem(STATISTICS_END_DATE)! : moment().endOf('month').format('YYYY-MM-DD')
    this.dateRange.push(startDate,endDate)
    await this.fetchCustomers()
    if(this.selectedCustomer && this.dateRange && this.dateRange.length > 1) {
      this.fetchStatistics(this.dateRange)
    }
  }

  mounted() {
    if(this.selectedCustomer && this.dateRange && this.dateRange.length > 1) {
      this.fetchStatistics(this.dateRange)
    }
  }
}
</script>

<style lang="stylus" scoped>
.subgroup-table
  width 100%
  border-collapse collapse
  border 1px solid silver

  th, td
    border 1px solid silver

  th
    font-weight bold

  .slot-column
    text-align left
    padding-left 4px
    width 50px

  .value-column
    text-align right
    padding-right 4px
    width: 70px
.row-color
  background-color #8fc0e7

.group-header
  background-color #2196F3
  color white

.card-header-small
  border-radius 0 !important
  margin-left -12px
  margin-right -12px

</style>
